import moment from "moment-timezone";

export default class NysmTimeSeries2 {
  static _domain = "api.nysmesonet.org";
  static _realtime_info = {};

  constructor(dataset, { stations = null, subset = null } = {}) {
    this._dataset = dataset;
    this._stations = stations;
    this._subset = subset;
    const rtkey = subset === null ? dataset : `${dataset}:${subset}`;
    if (!NysmTimeSeries2._realtime_info.hasOwnProperty(rtkey)) {
      NysmTimeSeries2._realtime_info[rtkey] = {
        polling: false,
        watchers: [],
        timerId: null,
        lastPoll: null,
        lastTimestamp: null,
        lastAnnounced: null,
      };
    }
    this._realtime = NysmTimeSeries2._realtime_info[rtkey];
    this._data_interval = moment.duration(5, "minutes");
    this._realtime_lag = moment.duration(10, "minutes");
    if (dataset === "nysm" && subset === "proc-winter-standard") {
      this._realtime_lag = moment.duration(30, "minutes");
    }
    this._realtime_poll_interval = moment.duration(30, "seconds");
  }

  // Adds a realtime data tracker. Only watcher per callback.
  // Provide a callback function.
  // The callback receives:
  //   arg 1: moment timestamp of new time
  //   arg 2: raw data response from realtime query
  add_realtime_tracker = (callbackFn) => {
    // Search for this watcher
    if (
      this._realtime.watchers.filter((w) => w.callback === callbackFn)
        .length === 0
    ) {
      this._realtime.watchers.push({
        callback: callbackFn,
      });
    } else {
      console.warn("Tracking requested, but watcher already registered.");
    }
    this._check_trackers(callbackFn);
  };

  // Remove realtime tracker. A tracker with the callback function is removed.
  remove_realtime_tracker = (callbackFn) => {
    const matches = this._realtime.watchers.filter(
      (w) => w.callback === callbackFn
    );
    const index =
      matches.length === 0 ? -1 : this._realtime.watchers.indexOf(matches[0]);
    if (index < 0) {
      console.warn(
        "Remove tracking requested, but watcher was not in the list."
      );
    } else {
      this._realtime.watchers.splice(index, 1);
    }
    this._check_trackers();
  };

  // Make sure we are polling or not polling depending on if there are watchers.
  _check_trackers = (new_callback = null) => {
    if (
      new_callback !== null &&
      this._realtime.polling &&
      this._realtime.lastAnnounced !== null
    ) {
      // If already tracking and a notification has already gone out
      // since we started tracking, go ahead and send that notification
      // to the new tracker.
      setTimeout(
        () =>
          new_callback(
            this._realtime.lastTimestamp,
            this._realtime.lastResponse
          ),
        0
      );
    }
    if (this._realtime.watchers.length > 0 && !this._realtime.polling) {
      // Start tracking and force a new poll
      this._realtime.polling = true;
      this._schedule_poll(true);
    }
    if (this._realtime.watchers.length === 0 && this._realtime.polling) {
      // Stop tracking.
      this._realtime.polling = false;
      this._stop_polling();
    }
  };

  // Schedule a poll
  _schedule_poll = (force = false) => {
    // Implementation of the poll
    const poll = () => {
      const now = moment();
      this._realtime.timerId = null;
      let do_poll = false;
      // Only do the poll if we can reasonably expect new data, or if forced.
      if (
        this._realtime.lastTimestamp === null ||
        this._realtime.lastPoll === null
      ) {
        do_poll = true;
      } else {
        do_poll = this._realtime.lastTimestamp
          .clone()
          .add(this.data_interval)
          .isSameOrBefore(now);
        do_poll &= this._realtime.lastPoll.isSameOrBefore(
          now.clone().subtract(this._realtime_poll_interval)
        );
      }
      do_poll |= force;
      let wait = null;
      if (!do_poll) {
        wait = new Promise((res) => res(null));
      } else {
        // We are doing the poll.
        let endpoint = this._dataset;
        if (this._subset) {
          endpoint += "/" + this._subset;
        }
        wait = fetch(
          `//${NysmTimeSeries2._domain}/data/dynserv/timeseries2/latest/${endpoint}`,
          {
            method: "GET",
          }
        )
          .then((response) => {
            if (response.ok) {
              return response.json();
            } else if (response.status === 404) {
              // Interpret as no new data
              // If data has not been announced since tracking began, then
              // send the old data.
              if (
                this._realtime.lastAnnounced === null &&
                this._realtime.lastTimestamp !== null
              ) {
                this._notify_watchers(
                  this._realtime.lastTimestamp,
                  this._realtime.lastResponse
                );
              }
              return null;
            } else {
              throw response;
            }
          })
          .then((response) => {
            if (response === null || !response.success) {
              return null;
            }
            this._realtime.lastPoll = now;
            const latest = moment.tz(response.response, "UTC");
            if (
              this._realtime.lastTimestamp === null ||
              latest.isAfter(this._realtime.lastTimestamp)
            ) {
              // This is a new time!
              this._realtime.lastTimestamp = latest;
              this._realtime.lastResponse = response;
              this._notify_watchers(latest, response);
            } else if (latest.isSame(this._realtime.lastTimestamp)) {
              // No new data
              // If data has not been announced since tracking began, then
              // send the old data.
              if (
                this._realtime.lastAnnounced === null &&
                this._realtime.lastTimestamp !== null
              ) {
                this._notify_watchers(
                  this._realtime.lastTimestamp,
                  this._realtime.lastResponse
                );
              }
              return null;
            } else {
              return null;
            }
          });
      }
      wait
        .then(() => {
          // Schedule the next poll
          if (this._realtime.polling) {
            this._schedule_poll();
          }
        })
        .catch((e) => {
          console.warn("Caught exception", e);
          if (this._realtime.polling) {
            this._schedule_poll();
          }
        });
    };

    if (force) {
      poll();
    }

    if (this._realtime.timerId) {
      clearTimeout(this._realtime.timerId);
      this._realtime.timerId = null;
    }

    this._realtime.timerId = setTimeout(poll, this._realtime_poll_interval);
  };

  // Called when all watchers have stopped watching.
  _stop_polling = () => {
    this._realtime.polling = false;
    this._realtime.lastAnnounced = null;
    if (this._realtime.timerId) {
      clearTimeout(this._realtime.timerId);
    }
    this._realtime.timerId = null;
  };

  // Send notification to all watchers.
  _notify_watchers(time, response) {
    this._realtime.lastAnnounced = time;
    this._realtime.watchers.forEach((w) =>
      setTimeout(() => w.callback(time, response), 0)
    );
  }

  // gets data within a range
  // Pass null for the times (or omit the times) for latest record.
  async get_data_in_time_range(variables, start_date = null, end_date = null) {
    let start =
      start_date === null
        ? null
        : moment.isMoment(start_date)
        ? start_date
        : moment.tz(start_date, "UTC");
    let end =
      end_date === null
        ? null
        : moment.isMoment(end_date)
        ? end_date
        : moment.tz(end_date, "UTC");

    if (!variables) {
      variables = [];
    }
    const request = {
      dataset: this._dataset,
      variables: variables,
      start:
        start === null ? "latest" : start.format("YYYY-MM-DDTHH:mm:ss.SSS"),
      end: end === null ? "latest" : end.format("YYYY-MM-DDTHH:mm:ss.SSS"),
    };
    if (this._stations) {
      request.stations = this._stations;
    }
    if (this._subset) {
      request.subset = this._subset;
    }
    try {
      let response = await fetch(
        "//" + NysmTimeSeries2._domain + "/data/dynserv/timeseries2",
        {
          headers: {
            "Content-Type": "application/json",
          },
          method: "POST",
          body: JSON.stringify(request),
        }
      ).then((response) => response.json());
      response = response.response;
      return response;
    } catch (err) {
      console.error(err);
      return null;
    }
  }

  // gets gridded data at a paticular time
  // Pass null for the times (or omit the times) for latest record.
  async get_griddata_at_time(variable, req_time = null) {
    let date =
      req_time === null
        ? null
        : moment.isMoment(req_time)
        ? req_time
        : moment.tz(req_time, "UTC");

    if (!variable) {
      throw "variable is required";
    }
    const date_str = date === null ? "latest" : date.format("YYYY-MM-DDTHH:mm:ss.SSS");
    const url = `//${NysmTimeSeries2._domain}/data/dynserv/grid/${this._dataset}/${variable}/${date_str}`;
    try {
      let response = await fetch(url).then((response) => response.json());
      response = response.response;
      return response;
    } catch (err) {
      console.error(err);
      return null;
    }
  }

  get dataset() {
    return this._dataset;
  }

  get subset() {
    return this._subset;
  }

  get data_interval() {
    return this._data_interval.clone();
  }

  get realtime_lag() {
    return this._realtime_lag.clone();
  }

  static _scale_array(arr, scale_factor) {
    if (scale_factor === 1) {
      return arr;
    }
    return arr.map((elem) => {
      if (Array.isArray(elem)) {
        return NysmTimeSeries2._scale_array(elem, scale_factor);
      }
      return elem === null ? null : elem * scale_factor;
    });
  }

  // Performs a transpose on arrays with order 2, sized MxN
  static _2d_transpose(arr) {
    return arr[0].map((col, i) => arr.map((row) => row[i]));
  }

  // This function mutates the input as follows:
  // 1. Any scale_factors are applied.
  // 2. Variables with a time index are re-ordered if necessary
  //    to put their time index as indicated by the time_index_position
  //    argument. "last" will put it last, and "first" will put it first.
  static adjust_response(response, time_index_position = "last") {
    if (!response.hasOwnProperty("data_vars")) {
      console.error("Missing data_vars property in response!");
      return;
    }
    Object.keys(response.coords).forEach((key) => {
      const dv = response.coords[key];
      // Adjust scale
      if (dv.attrs.hasOwnProperty("scale_factor")) {
        const scale = dv.attrs.scale_factor;
        dv.data = NysmTimeSeries2._scale_array(dv.data, scale);
        delete dv.attrs.scale_factor;
        const scale_str = scale.toString();
        const decimal_places =
          scale >= 1 ? 0 : scale_str.length - (scale_str.indexOf(".") + 1);
        dv.attrs.decimal_places = decimal_places;
      }
    });
    Object.keys(response.data_vars).forEach((key) => {
      const dv = response.data_vars[key];
      // Adjust scale
      if (dv.attrs.hasOwnProperty("scale_factor")) {
        const scale = dv.attrs.scale_factor;
        dv.data = NysmTimeSeries2._scale_array(dv.data, scale);
        delete dv.attrs.scale_factor;
        const scale_str = scale.toString();
        const decimal_places =
          scale >= 1 ? 0 : scale_str.length - (scale_str.indexOf(".") + 1);
        dv.attrs.decimal_places = decimal_places;
      }

      // Transpose dims if needed
      let time_index = dv.dims.indexOf("time");
      const order = dv.dims.length;
      if (time_index >= 0) {
        while (
          (time_index_position === "last" && time_index !== order-1) ||
          (time_index_position === "first" && time_index !== 0)
        ) {
          if (order === 2) {
            dv.data = NysmTimeSeries2._2d_transpose(dv.data);
            dv.dims = [dv.dims[1], dv.dims[0]];
            time_index = dv.dims.indexOf("time");
          } else {
            console.error("Needed to transpose a higher-order array");
            throw "Needed to transpose a higher-order array";
          }
        }
      }
      response.data_vars[key] = dv;
    });
  }
}
